<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { trackEvent } from '@/composables/useAnalytics'
import PersonalDetailsForm from '@/Components/Forms/PersonalDetailsForm.vue'
import SubjectTeachingSelection from '@/Components/Forms/SubjectTeachingSelection.vue'
import QualificationsForm from '@/Components/Forms/QualificationsForm.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'
import AltchaField from '@/Components/Forms/AltchaField.vue'

const props = defineProps({
  subjects: Array,
  levels: Array,
  examboards: Array,
  teachingExperiences: Array,
  qualificationOptions: Object,
  application: Object,
  slug: String,
})

const emit = defineEmits(['next', 'submit'])

// The first save creates the application and carries the bot check; the saves
// after it name the application by its slug.
const altcha = ref('')
const altchaField = ref(null)
const altchaNeededTest = ref(!props.application)
const applicationSlug = ref(props.slug)

// Personal details object - matching TutorApplication model
const personalDetails = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  address: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
  timezone: 'Europe/London',
})

/**
 * What the applicant teaches, one entry per subject.
 *
 * Subjects, course levels and exam boards used to be three sections, each
 * collecting an unrelated list, so the levels a tutor teaches a given subject at
 * were never actually asked. One entry here is
 * `{ subject_id, level_ids, examboard_ids }`; the server derives the three flat
 * lists it still stores from it.
 */
const teaching = ref([])

// Qualifications object - matching TutorApplication model
// New structure: degrees array + a_levels array (separated)
const qualifications = ref({
  degrees: [],
  a_levels: [],
  years_of_experience: '',
  teaching_experience_id: null,
  additional_qualifications: '',
})

// Component refs
const personalDetailsRef = ref(null)
const teachingRef = ref(null)
const qualificationsRef = ref(null)

// Loading and errors
const loading = ref(false)
const errors = ref({})

/**
 * What the sidebar reports. Each section is complete once the fields it names
 * are filled — the same fields the form validates on submit, so the tick and
 * the outcome of pressing Continue agree.
 */
const progressSections = computed(() => [
  {
    title: 'Basic Information',
    hint: 'Name, email, phone',
    complete: Boolean(
      personalDetails.value.first_name?.trim() &&
      personalDetails.value.last_name?.trim() &&
      personalDetails.value.email?.trim() &&
      personalDetails.value.phone?.trim()
    ),
  },
  {
    title: 'Subjects & Levels',
    hint: 'What you teach, and to whose syllabus',
    complete: teaching.value.some(
      (row) => row.subject_id && row.level_ids?.length && row.examboard_ids?.length
    ),
  },
  {
    title: 'Qualifications',
    hint: 'Degrees, A Levels, experience',
    complete: Boolean(
      qualifications.value.degrees?.some((degree) => degree.title?.trim() && degree.institution?.trim()) &&
      qualifications.value.years_of_experience
    ),
  },
])

// Populate form data from application prop
const populateFromApplication = (app) => {
  if (!app) return

  // Split name into first and last name
  const nameParts = app.name ? app.name.split(' ') : ['', '']
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  // Populate personal details
  personalDetails.value = {
    first_name: firstName,
    last_name: lastName,
    email: app.email || '',
    phone: app.phone || '',
    date_of_birth: app.date_of_birth || '',
    address: app.address || '',
    city: app.city || '',
    postcode: app.postcode || '',
    country: app.country || 'United Kingdom',
    timezone: app.timezone || 'Europe/London',
  }

  // Populate what the applicant teaches, falling back to the three lists an
  // application saved before the sections were merged.
  teaching.value = app.teaching?.length
    ? app.teaching.map((entry) => ({
        subject_id: entry.subject_id ?? null,
        level_ids: entry.level_ids ?? [],
        examboard_ids: entry.examboard_ids ?? [],
      }))
    : (app.subject_ids || []).map((subjectId) => ({
        subject_id: subjectId,
        level_ids: app.level_ids || [],
        examboard_ids: app.examboard_ids || [],
      }))

  // Populate qualifications - new structure with degrees array
  if (app.qualifications) {
    qualifications.value = {
      degrees: app.qualifications.degrees || [],
      a_levels: app.qualifications.a_levels || [],
      years_of_experience: app.qualifications.years_of_experience || '',
      teaching_experience_id: app.teaching_experience_ids?.[0] || null,
      additional_qualifications: app.additional_qualifications || '',
    }
  }
}

// Watch for application prop changes
watch(() => props.application, (newApp) => {
  if (newApp) {
    populateFromApplication(newApp)
  }
}, { immediate: true })

const updatePersonalDetails = (newDetails) => {
  personalDetails.value = newDetails
}

const validateForm = () => {
  const components = [
    personalDetailsRef,
    teachingRef,
    qualificationsRef
  ]

  let isValid = true

  // Validate all components
  components.forEach(compRef => {
    if (compRef.value && !compRef.value.validate()) {
      isValid = false
    }
  })

  return isValid
}

const saveDraft = async () => {
  // Don't validate for draft - just save whatever data is there
  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    // Add slug to header if available
    if (applicationSlug.value) {
      headers['X-Tutor-Slug'] = applicationSlug.value
    }

    const response = await fetch('/become-a-tutor/personal-information', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...(altchaNeededTest.value ? { altcha: altcha.value } : {}),
        is_draft: true, // Indicate this is a draft save
        // Personal details
        first_name: personalDetails.value.first_name,
        last_name: personalDetails.value.last_name,
        email: personalDetails.value.email,
        phone: personalDetails.value.phone,
        date_of_birth: personalDetails.value.date_of_birth,
        address: personalDetails.value.address,
        city: personalDetails.value.city,
        postcode: personalDetails.value.postcode,
        country: personalDetails.value.country,
        timezone: personalDetails.value.timezone,
        // What they teach, subject by subject
        teaching: teaching.value,
        // Qualifications - new structure with degrees array
        degrees: qualifications.value.degrees,
        a_levels: qualifications.value.a_levels,
        years_of_experience: qualifications.value.years_of_experience,
        teaching_experience_id: qualifications.value.teaching_experience_id,
        additional_qualifications: qualifications.value.additional_qualifications,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      altchaField.value?.reset()
      return
    }

    // Save slug to localStorage
    if (data.slug) {
      applicationSlug.value = data.slug
      altchaNeededTest.value = false
      localStorage.setItem('tutor_onboarding_slug', data.slug)
    }

    loading.value = false

    // Show success message
    // Says where the draft actually lives. It is keyed to a slug in
    // localStorage, so it does not follow the applicant to another
    // browser or device — "saved successfully" implied otherwise.
    errors.value = { success: 'Draft saved. It is kept in this browser on this device, so come back on the same one to carry on.' }
    setTimeout(() => {
      errors.value = {}
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred while saving draft. Please try again.' }
    loading.value = false
  }
}

const submitForm = async () => {
  if (!validateForm()) {
    // Scroll to the first visible error message
    setTimeout(() => {
      const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
      const firstVisibleError = Array.from(errorMessages).find(el =>
        el.offsetParent !== null && el.textContent.trim().length > 1
      )
      if (firstVisibleError) {
        firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    // Add slug to header if available
    if (applicationSlug.value) {
      headers['X-Tutor-Slug'] = applicationSlug.value
    }

    const response = await fetch('/become-a-tutor/personal-information', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...(altchaNeededTest.value ? { altcha: altcha.value } : {}),
        // Personal details
        first_name: personalDetails.value.first_name,
        last_name: personalDetails.value.last_name,
        email: personalDetails.value.email,
        phone: personalDetails.value.phone,
        date_of_birth: personalDetails.value.date_of_birth,
        address: personalDetails.value.address,
        city: personalDetails.value.city,
        postcode: personalDetails.value.postcode,
        country: personalDetails.value.country,
        timezone: personalDetails.value.timezone,
        // What they teach, subject by subject
        teaching: teaching.value,
        // Qualifications - new structure with degrees array
        degrees: qualifications.value.degrees,
        a_levels: qualifications.value.a_levels,
        years_of_experience: qualifications.value.years_of_experience,
        teaching_experience_id: qualifications.value.teaching_experience_id,
        additional_qualifications: qualifications.value.additional_qualifications,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      altchaField.value?.reset()

      // Scroll to the first visible error message
      setTimeout(() => {
        const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
        const firstVisibleError = Array.from(errorMessages).find(el =>
          el.offsetParent !== null && el.textContent.trim().length > 1
        )
        if (firstVisibleError) {
          firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }

    // Save slug and step to localStorage
    if (data.slug) {
      applicationSlug.value = data.slug
      altchaNeededTest.value = false
      localStorage.setItem('tutor_onboarding_slug', data.slug)
      localStorage.setItem('tutor_onboarding_step', '2')
    }

    loading.value = false

    trackEvent('tutorRegistrationBegin', { method: 'application_form' })

    // Emit next event to parent
    emit('next', data)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <div class="grid lg:grid-cols-4 gap-8">
    <!-- Left Sidebar - Only visible on large screens -->
    <aside id="sidebar-info" class="hidden lg:block lg:col-span-1">
      <div class="sticky top-24">
        <!-- Progress Indicator -->
        <div class="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 class="text-lg font-bold text-gray-900 mb-4">Application Progress</h3>
          <!--
            Real progress, not decoration.

            "Subjects & Levels" carried a permanently spinning icon and the other
            two were fixed at "done" and "not started" no matter what had been
            filled in — so the section that was working looked like the one stuck
            loading. Each line now ticks when its own fields are complete.
          -->
          <div class="space-y-4">
            <div
              v-for="(section, index) in progressSections"
              :key="section.title"
              class="flex items-start space-x-3"
            >
              <div
                class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                :class="section.complete ? 'bg-brand-blue' : 'bg-gray-200'"
              >
                <span v-if="section.complete" class="material-symbols-outlined text-white text-xs">check</span>
                <span v-else class="text-gray-400 text-xs">{{ index + 1 }}</span>
              </div>
              <div>
                <p class="font-semibold text-sm" :class="section.complete ? 'text-gray-900' : 'text-gray-500'">
                  {{ section.title }}
                </p>
                <p class="text-xs" :class="section.complete ? 'text-gray-500' : 'text-gray-400'">
                  {{ section.hint }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pro Tip Card -->
        <div class="bg-gradient-to-br from-brand-blue to-brand-purple rounded-2xl p-6 text-white">
          <span class="material-symbols-outlined text-3xl mb-3">lightbulb</span>
          <h3 class="text-lg font-bold mb-2">Pro Tip</h3>
          <p class="text-sm text-blue-100">Tutors who complete their profile fully get 3x more student requests!</p>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="lg:col-span-3">
      <form @submit.prevent="submitForm" novalidate class="space-y-8">
        <!-- Success Message -->
        <div v-if="errors.success" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">check_circle</span>
          </div>
          <div>
            <p class="text-green-900 font-semibold">Success</p>
            <p class="text-green-700 text-sm">{{ errors.success }}</p>
          </div>
        </div>

        <!-- General Error Message -->
        <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">error</span>
          </div>
          <div>
            <p class="text-red-900 font-semibold">Error</p>
            <p class="text-red-700 text-sm">{{ errors.general }}</p>
          </div>
        </div>

        <!-- Personal Details Component -->
        <PersonalDetailsForm
          ref="personalDetailsRef"
          :model-value="personalDetails"
          @update:model-value="updatePersonalDetails"
          :errors="errors"
        />

        <!-- Subjects, Levels and Exam Boards, asked subject by subject -->
        <SubjectTeachingSelection
          ref="teachingRef"
          :subjects="subjects"
          :levels="levels"
          :examboards="examboards"
          :model-value="teaching"
          @update:model-value="(val) => teaching = val"
          :errors="errors"
        />

    <!-- Qualifications Component -->
    <QualificationsForm
      ref="qualificationsRef"
      :model-value="qualifications"
      :teaching-experiences="teachingExperiences"
      :subjects="subjects"
      :qualification-options="qualificationOptions"
      @update:model-value="(val) => qualifications = val"
      :errors="errors"
    />

        <div v-if="altchaNeededTest">
          <AltchaField ref="altchaField" v-model="altcha" />
          <p v-if="errors.altcha" class="text-sm text-red-500 mt-1">{{ errors.altcha }}</p>
        </div>

      </form>
    </div>
  </div>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="false"
    primary-button-text="Continue to Services Offered"
    primary-button-loading-text="Saving..."
    @save-draft="saveDraft"
    @submit="submitForm"
  />
</template>
